import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDay,
  faUserCircle,
  faBoxArchive,
  faPen,
  faCheck,
  faEye,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../../../utils/dateUtils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, KebabMenu, PulseDot } from '../../../ui';
import { taskStatusMeta, type ClientTaskRow } from './taskMeta';

interface ExistingTasksListProps {
  clientTasks: ClientTaskRow[];
  isAdmin: boolean;
  handleStatusChange: (taskId: string, newStatus: string) => Promise<void>;
  handleArchiveTask: (taskId: string) => void;
  onViewTaskDetails: (task: ClientTaskRow) => void;
  onReorder: (tasks: ClientTaskRow[]) => void;
}

interface SortableTaskItemProps {
  task: ClientTaskRow;
  isAdmin: boolean;
  handleStatusChange: (taskId: string, newStatus: string) => Promise<void>;
  handleArchiveTask: (taskId: string) => void;
  onViewTaskDetails: (task: ClientTaskRow) => void;
}

function SortableTaskItem({
  task,
  isAdmin,
  handleStatusChange,
  handleArchiveTask,
  onViewTaskDetails,
}: SortableTaskItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const meta = taskStatusMeta(task.status);
  const isDone = task.status === 'Complete';
  const isSubmitted = task.status === 'Submitted';

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? 'var(--shadow-hover)' : 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 10px',
        margin: '0 -10px',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: isDragging ? 'var(--surface-raised)' : undefined,
        ...style,
      }}
      className={`hover-wash${isSubmitted ? ' ck-row-submitted' : ''}`}
      {...attributes}
      {...listeners}
      onClick={() => onViewTaskDetails(task)}
    >
      <PulseDot tone={meta.tone} pulse={meta.pulse} title={task.status} />
      <span style={{ flex: 1, minWidth: 0 }}>
        {/* Phones: the status badge leaves the title ~17 characters on one line, so it gets two. */}
        <span
          className="line-clamp-2 md:line-clamp-none md:block md:truncate"
          style={{
            fontSize: 'var(--text-sm)',
            color: isDone ? 'var(--text-faint)' : 'var(--text-body)',
            textDecoration: isDone ? 'line-through' : 'none',
            overflowWrap: 'anywhere',
          }}
        >
          {task.title}
        </span>
        {(task.due_date || task.assigned_to_name || task.website_title) && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '2px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
            }}
          >
            {task.due_date ? (
              <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '10px' }} />
                {formatDate(task.due_date, 'MMM d')}
              </span>
            ) : null}
            {task.assigned_to_name ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '10px' }} />
                {task.assigned_to_name}
              </span>
            ) : null}
            {task.website_title ? <span>{task.website_title}</span> : null}
          </span>
        )}
      </span>
      <Badge tone={meta.tone}>{task.status}</Badge>
      <KebabMenu
        label="Task actions"
        actions={[
          !isDone
            ? { label: 'Mark Complete', icon: faCheck, onClick: () => handleStatusChange(task.id, 'Complete') }
            : { label: 'Reopen', icon: faRotateLeft, onClick: () => handleStatusChange(task.id, 'In Progress') },
          !isDone && task.status !== 'Submitted'
            ? { label: 'Mark Submitted', icon: faEye, onClick: () => handleStatusChange(task.id, 'Submitted') }
            : null,
          { label: 'Edit Task', icon: faPen, onClick: () => onViewTaskDetails(task) },
          isAdmin
            ? { label: 'Archive', icon: faBoxArchive, danger: true, onClick: () => handleArchiveTask(task.id) }
            : null,
        ]}
      />
    </div>
  );
}

export default function ExistingTasksList({
  clientTasks,
  isAdmin,
  handleStatusChange,
  handleArchiveTask,
  onViewTaskDetails,
  onReorder,
}: ExistingTasksListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = clientTasks.findIndex((t) => t.id === active.id);
    const newIndex = clientTasks.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove([...clientTasks], oldIndex, newIndex);
    onReorder(reordered);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={clientTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {clientTasks.map((task) => (
            <SortableTaskItem
              key={task.id}
              task={task}
              isAdmin={isAdmin}
              handleStatusChange={handleStatusChange}
              handleArchiveTask={handleArchiveTask}
              onViewTaskDetails={onViewTaskDetails}
            />
          ))}
        </SortableContext>
      </DndContext>
      {clientTasks.length === 0 && (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          No tasks assigned yet. New tasks land here once they have an owner.
        </p>
      )}
    </div>
  );
}
